import React, { useEffect, useState } from 'react';
import { Key, Plus, RefreshCw, Trash2, Copy, Check, Eye, EyeOff } from 'lucide-react';
import apiKeysService from '../services/apiKeys';
import { useApps } from '../contexts/AppsContext';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';

const ApiKeysPage = () => {
    const { apps, loading: loadingApps } = useApps();
    const [appId, setAppId] = useState('');
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newKeyData, setNewKeyData] = useState(null); // shown after creation/rotation
    const [formData, setFormData] = useState({ key_name: '', expires_in_days: '' });
    const [copiedField, setCopiedField] = useState('');
    const [showSecret, setShowSecret] = useState(false);

    useEffect(() => {
        if (apps?.length > 0) {
            const defaultAppId = resolveDefaultAppId(apps);
            setAppId(defaultAppId);
            loadKeys(defaultAppId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps]);

    const loadKeys = async (targetAppId) => {
        if (!targetAppId) return;
        setLoading(true);
        setError('');
        try {
            const data = await apiKeysService.list(targetAppId);
            setKeys(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    const handleAppChange = (e) => {
        const id = e.target.value;
        setAppId(id);
        setSelectedAppId(id);
        if (id) loadKeys(id);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const payload = { key_name: formData.key_name || undefined };
            if (formData.expires_in_days) payload.expires_in_days = parseInt(formData.expires_in_days);
            const res = await apiKeysService.create(appId, payload);
            setNewKeyData(res.data);
            setShowCreateModal(false);
            setFormData({ key_name: '', expires_in_days: '' });
            loadKeys(appId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to create API key');
        }
    };

    const handleRotate = async (apiKeyId) => {
        if (!window.confirm('Rotate this key? The old key/secret will stop working immediately.')) return;
        try {
            const res = await apiKeysService.rotate(apiKeyId);
            setNewKeyData(res.data);
            setSuccessMessage('Key rotated!');
            loadKeys(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to rotate');
        }
    };

    const handleDelete = async (apiKeyId) => {
        if (!window.confirm('Delete this API key? It will be deactivated immediately.')) return;
        try {
            await apiKeysService.remove(apiKeyId);
            setSuccessMessage('API key deleted.');
            loadKeys(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete');
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 2000);
    };

    return (
        <div className="space-y-4">
            <PageHeader
                title="API Keys"
                subtitle="Manage API key/secret pairs for basic auth access"
                actions={
                    <div className="flex gap-2">
                        <select
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[200px]"
                            value={appId}
                            onChange={handleAppChange}
                            disabled={loadingApps}
                        >
                            <option value="">Select an App</option>
                            {apps.map((app) => (
                                <option key={app.app_id} value={app.app_id}>
                                    {app.app_name}
                                </option>
                            ))}
                        </select>
                        {appId && (
                            <button
                                onClick={() => {
                                    setFormData({ key_name: '', expires_in_days: '' });
                                    setShowCreateModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4" /> Generate Key
                            </button>
                        )}
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                    {successMessage}
                </div>
            )}

            {/* New Key Display (shown after create/rotate) */}
            {newKeyData && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-5 space-y-3">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                        <Key className="h-4 w-4" />
                        Save these credentials — the secret will not be shown again!
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500 w-20">API Key:</span>
                            <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                {newKeyData.api_key}
                            </code>
                            <button
                                onClick={() => copyToClipboard(newKeyData.api_key, 'key')}
                                className="p-1 rounded hover:bg-amber-100"
                            >
                                {copiedField === 'key' ? (
                                    <Check className="h-4 w-4 text-emerald-600" />
                                ) : (
                                    <Copy className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500 w-20">Secret:</span>
                            <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                {showSecret ? newKeyData.api_secret : '•'.repeat(40)}
                            </code>
                            <button
                                onClick={() => setShowSecret(!showSecret)}
                                className="p-1 rounded hover:bg-amber-100"
                            >
                                {showSecret ? (
                                    <EyeOff className="h-4 w-4 text-slate-500" />
                                ) : (
                                    <Eye className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                            <button
                                onClick={() => copyToClipboard(newKeyData.api_secret, 'secret')}
                                className="p-1 rounded hover:bg-amber-100"
                            >
                                {copiedField === 'secret' ? (
                                    <Check className="h-4 w-4 text-emerald-600" />
                                ) : (
                                    <Copy className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                        </div>
                    </div>
                    <button onClick={() => setNewKeyData(null)} className="text-xs text-amber-700 hover:underline">
                        Dismiss
                    </button>
                </div>
            )}

            {loading ? (
                <SkeletonList />
            ) : error ? (
                <ErrorState message={error} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 p-8 text-center">
                    <p className="text-slate-500">Select an App to manage API keys.</p>
                </div>
            ) : keys.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 p-8 text-center">
                    <Key className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No API keys yet. Generate one to enable basic auth.</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {keys.map((k) => (
                        <div
                            key={k.api_key_id}
                            className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm space-y-3"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-600 grid place-content-center">
                                        <Key className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-900">
                                            {k.key_name || 'Unnamed Key'}
                                        </div>
                                        <code className="text-xs text-slate-500 font-mono">
                                            {k.api_key.substring(0, 16)}...
                                        </code>
                                    </div>
                                </div>
                                <span
                                    className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                                        k.is_active
                                            ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                                            : 'text-slate-500 bg-slate-50 border-slate-200'
                                    }`}
                                >
                                    {k.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className="text-xs text-slate-500 space-y-1">
                                <div>Created: {new Date(k.created_on).toLocaleDateString()}</div>
                                {k.last_used_at && (
                                    <div>Last used: {new Date(k.last_used_at).toLocaleDateString()}</div>
                                )}
                                {k.expires_at && <div>Expires: {new Date(k.expires_at).toLocaleDateString()}</div>}
                            </div>
                            {k.is_active && (
                                <div className="flex gap-2 pt-2 border-t border-slate-100">
                                    <button
                                        onClick={() => handleRotate(k.api_key_id)}
                                        className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 flex items-center justify-center gap-1"
                                    >
                                        <RefreshCw className="h-3 w-3" /> Rotate
                                    </button>
                                    <button
                                        onClick={() => handleDelete(k.api_key_id)}
                                        className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 flex items-center justify-center gap-1"
                                    >
                                        <Trash2 className="h-3 w-3" /> Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create Key Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Generate API Key"
                size="sm"
            >
                <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Key Name (optional)</label>
                        <input
                            type="text"
                            value={formData.key_name}
                            onChange={(e) => setFormData({ ...formData, key_name: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="e.g., Production API"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Expires in (days, optional)
                        </label>
                        <input
                            type="number"
                            min="1"
                            value={formData.expires_in_days}
                            onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="Leave empty for no expiry"
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Generate
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ApiKeysPage;
